import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { AccountService } from '@app/core/services/account.service';
import { AuthService } from '@app/core/services/auth.service';
import { Theme } from '@app/core/services/theme';
import { ThemeService } from '@app/core/services/theme.service';
import {
  ShellSubnavComponent,
  ShellSubnavLink,
} from '@app/shared/components/shell-subnav/shell-subnav.component';
import { filter } from 'rxjs';
import { User } from '@app/core/models/user.model';
import { EmailVerificationBannerComponent } from '@app/shared/components/email-verification-banner/email-verification-banner';
import { AccountAccessNoticeComponent } from '@app/shared/components/account-access-notice/account-access-notice.component';

interface NavLink {
  label: string;
  route: string;
  icon: string;
  description?: string;
  exact?: boolean;
}

type LocalChrome = { left: string; right?: string } | null;

@Component({
  selector: 'app-app-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, FormsModule, ShellSubnavComponent, EmailVerificationBannerComponent, AccountAccessNoticeComponent],
  templateUrl: './app-layout.component.html',
})
export class AppLayoutComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly accountService = inject(AccountService);
  readonly themeService = inject(ThemeService);

  readonly availableMemberships = this.accountService.availableMemberships;
  readonly user = signal<User | null>(null);
  readonly activeAccountId = signal(this.accountService.getActiveAccountId());
  readonly activeAccount = computed(() => {
    const accountId = this.activeAccountId();
    if (!accountId) {
      return null;
    }

    return (
      this.accountService
        .memberships()
        .find(
          (membership) =>
            membership.account_id === accountId || membership.account?.id === accountId,
        )?.account ?? null
    );
  });
  readonly activeAccountName = computed(() => {
    const account = this.activeAccount();
    if (account) {
      return account.name;
    }

    return this.activeAccountId() ? 'Loading account…' : 'No active account';
  });
  readonly currentUrl = signal(this.router.url);
  readonly profileDrawerOpen = signal(false);
  readonly currentPath = computed(() => this.currentUrl().split('?')[0] || '/');
  readonly isLiveTailRoute = computed(() => this.currentPath().startsWith('/live-tail'));
  readonly primaryLinks: NavLink[] = [
    { label: 'Tail', route: '/live-tail', icon: 'tabler--activity-heartbeat', exact: false },
  ];

  readonly profileLinks: NavLink[] = [
    { label: 'Profile settings', route: '/settings/profile', icon: 'tabler--user-circle' },
  ];

  readonly workspaceTabs: ShellSubnavLink[] = [
    { label: 'Dash', route: '/dashboard', icon: 'tabler--layout-dashboard', exact: true },
    { label: 'Streams', route: '/log-streams', icon: 'tabler--wave-sine' },
    { label: 'Receivers', route: '/log-receivers', icon: 'tabler--inbox' },
    { label: 'Usage', route: '/usage', icon: 'tabler--chart-histogram' },
    { label: 'Access tokens', route: '/access-tokens', icon: 'tabler--key' },
    { label: 'Sources', route: '/settings/sources', icon: 'tabler--cloud-download' },
    { label: 'Integrations', route: '/settings/integrations', icon: 'tabler--plug-connected' },
    { label: 'Profile', route: '/settings/profile', icon: 'tabler--user-circle' },
    {
      label: 'Account',
      route: '/settings/account',
      icon: 'tabler--building-bank',
      matchPrefixes: ['/settings/security'],
    },
    { label: 'Team', route: '/settings/team', icon: 'tabler--users-group' },
    { label: 'Billing', route: '/settings/billing', icon: 'tabler--credit-card' },
    { label: 'Invitations', route: '/invitations', icon: 'tabler--mail-plus' },
  ];

  readonly localChrome = computed<LocalChrome>(() => {
    const path = this.currentPath();
    if (path === '/log-receivers/create') {
      return { left: 'Receivers / Create' };
    }

    if (/^\/log-receivers\/[^/]+\/edit$/.test(path)) {
      return { left: 'Receivers / Edit' };
    }

    if (path === '/log-streams/create') {
      return { left: 'Streams / Create' };
    }

    if (/^\/log-streams\/[^/]+\/edit$/.test(path)) {
      return { left: 'Streams / Edit' };
    }

    if (path === '/access-tokens/create') {
      return { left: 'Access tokens / Create' };
    }

    if (/^\/access-tokens\/[^/]+\/edit$/.test(path)) {
      return { left: 'Access tokens / Edit' };
    }

    return null;
  });

  constructor() {
    this.accountService.activeAccount$
      .pipe(takeUntilDestroyed())
      .subscribe((accountId) => this.activeAccountId.set(accountId));

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed())
      .subscribe((event: NavigationEnd) => {
        this.currentUrl.set(event.urlAfterRedirects || event.url);
        this.closeMenus();
      });

    this.authService
      .getUser()
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this.user.set(user);
      });
  }

  switchAccount(accountId: string): void {
    this.closeMenus();
    this.accountService.switchAccount(accountId);
  }

  toggleProfileDrawer(): void {
    this.profileDrawerOpen.update((open) => !open);
  }

  closeMenus(): void {
    this.profileDrawerOpen.set(false);
  }

  setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }

  logout(): void {
    this.authService.logout();
    this.closeMenus();
    void this.router.navigate(['/login']);
  }

  isActiveRoute(route: string, exact = false): boolean {
    const path = this.currentPath();
    return exact ? path === route : path === route || path.startsWith(`${route}/`);
  }
}
